import { GoalPeriod } from '@akin-home/amplify';
import { format, startOfWeek } from 'date-fns';
import { goalPeriodTexts } from '../data';
import { Goal } from '../types';
import { createGoalModel } from './createGoal';

export * from './createGoal';
export * from './getCompletionRateSample';
export * from './getGoalMonthGridData';
export * from './getGoalProductivityData';
export * from './incrementGoalCompletion';

export const createGoalDateKey = (date: Date, period: GoalPeriod): string => {
  switch (period) {
    case GoalPeriod.day:
      return format(date, 'yyyy-MM-dd');

    case GoalPeriod.week:
      return format(startOfWeek(date), 'yyyy-ww');

    case GoalPeriod.month:
      return format(date, 'yyyy-MM');

    case GoalPeriod.year:
      return format(date, 'yyyy');
  }
};

export const setGoalTitle = (title: string) => (goal: Goal) => ({
  ...goal,
  title,
});

export const setGoalEmoji = (emoji: string) => (goal: Goal) => ({
  ...goal,
  emoji,
});

export const increaseGoalCompletions = (goal: Goal): Goal => ({
  ...goal,
  completions_per_period: goal.completions_per_period + 1,
});

export const decreaseGoalCompletions = (goal: Goal): Goal => ({
  ...goal,
  completions_per_period: Math.max(goal.completions_per_period - 1, 1),
});

export const setGoalPeriod =
  (period: GoalPeriod) =>
  (goal: Goal): Goal => ({
    ...goal,
    period,
  });

/** Gets the display text for goal frequency */
export const getGoalFrequencyDisplayText = ({
  completions_per_period,
  period,
}: Goal): string => {
  const prefixes: { [key: number]: string } = {
    1: 'Once',
    2: 'Twice',
  };

  const prefix =
    prefixes[completions_per_period] || `${completions_per_period} times`;

  return `${prefix} a ${period}`;
};

/** Creates a key for goal completion record */
export const createGoalKey = ({
  date,
  period,
  user_id,
}: {
  date: Date;
  period: GoalPeriod;
  user_id: string;
}): string => `${createGoalDateKey(date, period)}#${user_id}`;

/** Gets the goal completion progress for a given date and user */
export const getGoalCompletionProgress = (
  { period, completions }: Goal,
  { date, user_id }: { date: Date; user_id: string }
): number =>
  completions[createGoalKey({ date, user_id, period })]?.progress || 0;

export const getGoalProgressPercentage = (
  goal: Goal,
  data: { date: Date; user_id: string }
): number => {
  const percentage =
    (getGoalCompletionProgress(goal, data) / goal.completions_per_period) * 100;
  return Math.min(percentage, 100);
};

export const getGoalPeriodText = (period: GoalPeriod): string =>
  goalPeriodTexts[period];

export const toggleCategoryInGoal = (params: {
  category_id: string;
  goal: Goal;
}): Goal => {
  const { category_id, goal } = params;

  return {
    ...goal,
    category_ids: goal.category_ids.includes(category_id)
      ? goal.category_ids.filter((id) => id !== category_id)
      : [...goal.category_ids, category_id],
  };
};

/** Takes a goal template and generates and instance from it */
export const generateGoalInstanceFromTemplate = (goal: Goal): Goal => ({
  ...goal,
  id: '',
  reference_code: goal.reference_code,
  isTemplate: false,
  readGroups: [],
});

export const getDefaultValuesFromGoalTemplate = (
  goal: Goal
): Partial<Goal> => ({
  title: goal.title,
  emoji: goal.emoji,
  period: goal.period,
  completions_per_period: goal.completions_per_period,
  reference_code: goal.reference_code,
});

/** Increments goal completion */
export const incrementGoalCompletion = (
  goal: Goal,
  data: { date: Date; user_id: string },
  incrementVal = 1
): Goal => {
  const key = createGoalKey({ ...data, period: goal.period });

  const current_progress = goal.completions[key]?.progress || 0;

  const new_value = current_progress + incrementVal;

  // Increment the completion, but don't go over the max limit at this stage
  let progress = Math.min(new_value, goal.completions_per_period);

  /** Don't allow going below 0 */
  if (progress < 0) {
    progress = 0;
  }

  return {
    ...goal,
    completions: {
      ...goal.completions,
      [key]: {
        progress,
      },
    },
  };
};

export const createGoalFromTemplate = (
  goal: Goal,
  data: { habitatID: string; participant_ids: string[] }
): Goal =>
  createGoalModel({ ...goal, readGroups: [], isTemplate: false, ...data });

export const filterGoalsByPeriod = (period: GoalPeriod, goals: Goal[] | undefined) =>
  goals?.filter((g) => g.period === period);

export const getGoalRemainingProgress = (
  goal: Goal,
  key: { date: Date; user_id: string }
) => {
  return goal.completions_per_period - getGoalCompletionProgress(goal, key);
};
